import { Column, DataTable, DataTableContainer } from "components/DataTable";

import moment from "moment";
import { StatusBadge } from "./StatusBadge";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useModal } from "../../../hooks/useModal";
import dynamic from "next/dynamic";
import { TableActionButton } from "../../DataTable";
import { faDatabase } from "@fortawesome/free-solid-svg-icons";
import { InlineTabBar } from "@ui";

const MessageHistory = dynamic(() => import("./MessageHistory"));

interface Props {
    clubSlug: string;
}

type View = "messages" | "system" | "undeliverable";

export const MessageList = ({ clubSlug }: Props) => {
    const { t } = useTranslation("message-management");
    const [openModal, resetModal] = useModal();
    const [view, setView] = useState<View>("messages");
    const tabs = useMemo(() => [
        { key: "messages", label: t("nav.messages") },
        { key: "system", label: t("nav.systemMails") },
        { key: "undeliverable", label: t("nav.undeliverable") }
    ], [t]);
    const openMessageStatusHistory = useCallback((messageId: number) =>
            openModal({
                title: t("list.statusHistoryTitle"),
                content: <MessageHistory messageId={messageId} />,
                size: 50,
                show: true
            })
        , [t]);

    const columns: Column[] = useMemo(() => [
        {
            name: t("list.columns.to"),
            uid: "to",
            allowsSorting: true,
            renderer: (value) => {
                if (Array.isArray(value) && value.length > 0) {
                    const address = value?.[0];
                    let addressString = `${address?.address || ""}`;
                    if (address?.name) addressString += ` - ${address?.name}`;
                    if (value.length > 1) addressString += ` | + ${value.length - 1}`;

                    return addressString;
                }

                return Object.entries(value).map(([addr, name]) => addr + (name ? ` - ${name}` : "")).join(" | ");
            }
        },
        { name: t("list.columns.subject"), uid: "subject", allowsSorting: true },
        {
            name: t("list.columns.lastStatus"),
            uid: "status",
            renderer(value, item?) {
                const last = value?.[0];
                return <StatusBadge status={last} messageId={item.id} />;
            }
        },
        {
            name: t("list.columns.sentAt"),
            uid: "created_at",
            allowsSorting: true,
            renderer(value, item?) {
                return moment(value).format("lll");
            }
        },
        { name: t("list.columns.actions"), uid: "actions", align: "end" }
    ], [t]);

    return <>
        <InlineTabBar className="tw-mb-4" activeTab={view} onChange={(key) => setView(key as View)} tabs={tabs} />
        <DataTableContainer>
        {/* the list loads once per mount, so a new view needs a fresh table */}
        <DataTable key={view} tableProps={{
            sticked: true,
            bordered: true,
            borderWeight: "light",
            compact: false
        }} columns={columns} fetchUrl={"message-management/" + clubSlug} fetchData={{ view }} fetchMethod="GET"
            rowActions={(item) => <TableActionButton.Container justify="flex-end">
                <TableActionButton icon={faDatabase} onPress={() => openMessageStatusHistory(item.id)}>
                    {t("list.statusHistoryButton")}
                </TableActionButton>
            </TableActionButton.Container>}
        />
        </DataTableContainer>
    </>;
};
